import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Folder, Upload, Download, FileText, Trash2, Loader2 } from "lucide-react";

const ICON_BY_EXT = {
  pdf: "PDF", docx: "DOC", doc: "DOC", xlsx: "XLS", xls: "XLS",
  pptx: "PPT", ppt: "PPT", png: "IMG", jpg: "IMG", jpeg: "IMG",
};
const ACCEPT = ".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/png,image/jpeg";
const MAX_BYTES = 10 * 1024 * 1024;

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Shared document hub. Used by Clients, Competitors, Vendors and the three Formats modules.
 * document_upload_fix_spec.md §3-7A
 */
export default function DocumentHub({ module, eyebrow, title, subtitle, categoryItems = [] }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tag, setTag] = useState("");
  const [categoryItem, setCategoryItem] = useState(categoryItems[0]?.value || "");
  const inputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/hub`, { params: { module, ...(categoryItem ? { category_item: categoryItem } : {}) } });
      setDocs(data || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [module, categoryItem]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum is 10 MB.`);
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!Object.keys(ICON_BY_EXT).includes(ext)) {
      toast.error(`Unsupported file type ".${ext}". Allowed: PDF, DOCX, XLSX, PPTX, PNG, JPG.`);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("module", module);
    if (tag) fd.append("tag", tag);
    if (categoryItem) fd.append("category_item", categoryItem);
    try {
      await api.post(`/documents/hub`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message || "Upload failed — please try again.");
    } finally { setUploading(false); }
  };

  const onDownload = async (doc) => {
    try {
      const resp = await api.get(`/documents/${doc.id}/secure-download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted");
      refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div className="space-y-8" data-testid={`dochub-${module}`}>
      <div className="flex items-end justify-between border-b border-ink-line pb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand text-white grid place-items-center rounded-sm">
            <Folder className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">{eyebrow}</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{title}</h1>
            {subtitle && <p className="text-sm text-ink-muted mt-1.5">{subtitle}</p>}
          </div>
        </div>
      </div>

      <div className="card-flat p-5 space-y-3">
        {categoryItems.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="stat-label shrink-0">Item</div>
            <div className="flex flex-wrap gap-1">
              {categoryItems.map((c) => (
                <button key={c.value} onClick={() => setCategoryItem(c.value)} data-testid={`dochub-cat-${c.value}`}
                  className={`px-3 py-1.5 text-[12px] uppercase tracking-wider rounded-sm border transition-colors ${
                    categoryItem === c.value ? "bg-brand text-white border-brand" : "border-ink-line text-ink-muted hover:bg-secondary"
                  }`}>{c.label}</button>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={inputRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden" data-testid={`dochub-input-${module}`} />
          <button onClick={onPick} disabled={uploading} data-testid={`dochub-upload-${module}`}
            className="px-4 py-2.5 text-sm bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload</>}
          </button>
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Tag (optional)…"
            data-testid={`dochub-tag-${module}`}
            className="px-3 py-2 text-sm border border-ink-line bg-white rounded-sm flex-1 max-w-xs focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" />
          <div className="text-[11px] text-ink-muted">PDF · DOCX · XLSX · PPTX · PNG · JPG (max 10 MB)</div>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">Filename</th>
              {categoryItems.length > 0 && <th className="px-5 py-3 font-medium">Item</th>}
              <th className="px-5 py-3 font-medium">Tag</th>
              <th className="px-5 py-3 font-medium text-right">Size</th>
              <th className="px-5 py-3 font-medium">Uploaded</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="px-5 py-10 text-center text-ink-muted uppercase tracking-wider text-xs">Loading…</td></tr>)}
            {!loading && docs.length === 0 && (<tr><td colSpan={6} className="px-5 py-10 text-center text-ink-muted">No documents yet. Use Upload to add one.</td></tr>)}
            {docs.map((d) => {
              const ext = (d.original_filename || "").split(".").pop().toLowerCase();
              return (
                <tr key={d.id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="chip text-[9px] border-ink-line bg-secondary text-ink-muted font-num">{ICON_BY_EXT[ext] || ext.toUpperCase()}</span>
                      <span className="font-medium truncate max-w-md" title={d.original_filename}>{d.original_filename}</span>
                    </div>
                  </td>
                  {categoryItems.length > 0 && <td className="px-5 py-3 text-ink-muted text-xs">{d.category_item || "—"}</td>}
                  <td className="px-5 py-3 text-ink-muted text-xs">{d.tag || "—"}</td>
                  <td className="px-5 py-3 text-right font-num text-ink-muted text-xs">{fmtSize(d.size)}</td>
                  <td className="px-5 py-3 text-ink-muted font-num text-xs">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => onDownload(d)} title="Download" data-testid={`dochub-dl-${d.id}`}
                        className="p-1.5 text-ink-muted hover:text-brand hover:bg-secondary rounded-sm transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => onDelete(d)} title="Delete" data-testid={`dochub-del-${d.id}`}
                        className="p-1.5 text-ink-muted hover:text-warn hover:bg-warn-soft rounded-sm transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
